/**
 * useWidgetData.js — Tiny helper that fetches a widget's payload, re-fetches
 * on settings change, and re-fetches when an invalidation event matches
 * any of the widget's topics.
 *
 * #334: `loading()` only reflects the *initial* load (no data yet). Once a
 * payload has arrived it stays false across subsequent re-fetches so the
 * existing `<Show when={!loading()}>` gates in every widget keep showing
 * the last good data instead of blanking to "Loading…". The new
 * `refreshing()` signal is true any time a fetch is in flight and is
 * exposed for widgets that want a subtle header pulse / micro-spinner.
 */
import { createSignal, createEffect, onCleanup } from 'solid-js';
import api, { describeApiError } from '../lib/apiClient.js';
import wm from '../lib/wmStore.js';

export default function useWidgetData(type, paramsAccessor) {
  const [data, setData] = createSignal(null);
  const [loading, setLoading] = createSignal(false);
  const [refreshing, setRefreshing] = createSignal(false);
  const [error, setError] = createSignal(null);

  // Coalesce overlapping fetches: while one is in flight, a second trigger
  // just marks that one more pass is needed and lets the running call do it.
  let _inFlight = false;
  let _again = false;

  async function refetch() {
    if (_inFlight) { _again = true; return; }
    _inFlight = true;
    setRefreshing(true);
    // Only flip the public "loading" flag when there's no data to keep on
    // screen. Subsequent refreshes preserve the last payload so widgets
    // never blank to "Loading…" once they've shown content (#334).
    if (data() == null) setLoading(true);
    setError(null);
    try {
      const r = await api.fetchWidget(type, paramsAccessor());
      // Guard against a 304 / not-modified shape so we never blank the
      // widget with an undefined payload when the server cache returns a
      // stable ETag.
      if (r && r.payload !== undefined) setData(r.payload);
    } catch (err) {
      setError(describeApiError(err, 'Widget data'));
    } finally {
      setLoading(false);
      setRefreshing(false);
      _inFlight = false;
      if (_again) { _again = false; refetch(); }
    }
  }

  createEffect(() => {
    paramsAccessor();
    refetch();
  });

  // Re-fetch on relevant invalidations, debounced so a burst of events
  // (e.g. several sync batches landing in quick succession) collapses into a
  // single refetch instead of one fetch per event.
  const meta = wm.state.catalog.find(c => c.type === type);
  const topics = new Set(meta?.topics || []);
  if (topics.size) {
    let _debounce = null;
    let _dirtyWhileHidden = false;
    const onVisChange = () => {
      if (typeof document !== 'undefined' && !document.hidden && _dirtyWhileHidden) {
        _dirtyWhileHidden = false;
        refetch();
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisChange);
    }
    const off = api.subscribeEvents((evt) => {
      if (!topics.has(evt.topic)) return;
      if (typeof document !== 'undefined' && document.hidden) {
        _dirtyWhileHidden = true;
        return;
      }
      clearTimeout(_debounce);
      _debounce = setTimeout(() => { refetch(); }, 250);
    });
    onCleanup(() => {
      clearTimeout(_debounce);
      off();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisChange);
      }
    });
  }

  return { data, loading, refreshing, error, refetch };
}
